import { Link, useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';


function Registration() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [teamName, setTeamName] = useState('');
  const [manualFplId, setManualFplId] = useState('');
  const [foundFplId, setFoundFplId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) setReferralCode(ref);
  }, [location]);
  
  function normalizePhone(phone) {
    const trimmed = phone.trim();

    let normalized;

    if (/^0\d{9}$/.test(trimmed)) {
      normalized = "254" + trimmed.slice(1);
    } else if (/^7\d{8}$/.test(trimmed)) {
      normalized = "254" + trimmed;
    } else if (/^254\d{9}$/.test(trimmed)) {
      normalized = trimmed;
    } else {
      return null;
    }

    // Valid Kenyan mobile prefixes
    if (!/^254(7|1)\d{8}$/.test(normalized)) {
      return null;
    }

    return normalized;
  }

  const handleStepOne = (e) => {
    e.preventDefault();
    const trimmedPassword = password.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const normalizedPhone = normalizePhone(phone);

    if (!trimmedEmail) {
      alert("Email is required.");
      return;
    }

    if (!normalizedPhone) {
      alert("Please enter a valid Kenyan phone number.");
      return;
    }

    
    if (trimmedPassword.length < 6) {
      alert("Password must be at least 6 characters long.");
      return;
    }


    setEmail(trimmedEmail);
    setPhone(normalizedPhone); 
    setStep(2);
  };

  
  const handleManualFplSubmit = (e) => {
    e.preventDefault();
    if (!/^\d+$/.test(manualFplId.trim())) {
      alert("FPL ID must be numeric.");
      return;
    }
    setFoundFplId(manualFplId.trim());
  };

  const handleCompleteRegistration = async () => {
    if (!foundFplId) return alert("FPL ID is required.");

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          phone,
          username,
          password,
          fplId: foundFplId,
          ref: referralCode || undefined,
        }),
        credentials: "include",
      });

      const data = await res.json();

      if (res.status === 400 || res.status === 403) {
        alert(data.message || "Registration failed");
        setLoading(false);
        return;
      }

      setLoading(false);
      navigate("/login");
    } catch (err) {
      console.error(err);
      alert("Server error during registration.");
      setLoading(false);
    }
  };

  const handleTeamSearch = (query) => {
    clearTimeout(searchTimeout.current);

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(`${API_URL}/find-fpl-id`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ teamName: query }),
        });
        const data = await res.json();
        if (res.ok) setSearchResults(data);
        else setSearchResults([]);
      } catch (err) {
        console.error(err);
        setSearchResults([]);
      }
      setSearchLoading(false);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-[#2d006b] flex flex-col justify-center items-center font-sans">
      {loading && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-sm flex justify-center items-center z-[9999]">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-400 rounded-full animate-spin"></div>
        </div>
      )}

      <div className={`bg-white rounded-2xl flex flex-col items-center p-7 w-full max-w-[420px] relative ${loading ? 'blur-sm pointer-events-none select-none' : ''}`}>
        <h2 className="text-xl font-semibold text-gray-800 text-center mb-4">Create FPL365 Account</h2>


        {step === 1 && (
           <form onSubmit={handleStepOne} className="flex flex-col w-full">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full bg-gray-100 border border-gray-200 rounded-xl py-3 px-4 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#2d006b]"
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone Number eg. 07XXXXXXXX"
              required
              className="w-full bg-gray-100 border border-gray-200 rounded-xl py-3 px-4 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#2d006b]"
            />
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              required
              maxLength={12}
              className="w-full bg-gray-100 border border-gray-200 rounded-xl py-3 px-4 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#2d006b]"
            />
            <div className="relative mb-3">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="w-full bg-gray-100 border border-gray-200 rounded-xl py-3 pl-4 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d006b]"
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            <button type="submit" className="bg-[#2d006b] text-white rounded-xl py-3 w-full font-semibold mt-2 hover:bg-[#3d0090] transition-colors">
              Continue
            </button>
           <p className="text-gray-500 text-sm text-center font-normal mt-3">
              Already have an account? <Link to="/login" className="no-underline text-[#2d006b] font-semibold">Login</Link>
            </p>
          </form>
        )}

        {step === 2 && (
          <div className="flex flex-col w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Step 2: Join Fpl Our League</h3>
             <p className="text-sm text-gray-500 mb-3">
              Click below to join our league. After joining, return here and enter your team name.
            </p>
            <div className="flex justify-center items-center mb-4">
              <button
                type="button"
                onClick={() =>
                  window.open(
                    "https://fantasy.premierleague.com/leagues/auto-join/xinnvz",
                    "_blank"
                  )
                }
                className="bg-gradient-to-br from-[#2d006b] to-[#20cde4] text-white rounded-xl py-3 px-6 font-semibold shadow-lg shadow-cyan-400/30 hover:shadow-xl hover:shadow-cyan-400/50 transition-all border-none "
            >
              Join FPL League
            </button>
            </div>
            

            <hr className="w-full border-gray-200 my-4" />

            <div className="relative w-full mb-3">
              <input
                type="text"
                value={teamName}
                onChange={(e) => {
                  const query = e.target.value;
                  setTeamName(query);
                  setFoundFplId(null);
                  handleTeamSearch(query);
                }}
                placeholder="Search your FPL team..."
                autoComplete="off"
                className="w-full bg-gray-100 border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d006b]"
              />

              {searchLoading && (
                <div className="absolute top-full mt-2 bg-white p-2 rounded-lg shadow-md text-sm text-gray-500">
                  Loading...
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-lg max-h-[250px] overflow-y-auto z-50">
                  {searchResults.map((team) => (
                    <div
                      key={team.entry}
                      onClick={() => {
                        setTeamName(team.entry_name);
                        setFoundFplId(team.entry);
                        setSearchResults([]);
                      }}
                      className="px-4 py-3 text-sm cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <strong>{team.entry_name}</strong> | Manager: {team.player_name} | Total Points: {team.total}
                    </div>
                  ))}
                </div>
              )}
            </div>
            

            <p className="text-sm font-semibold text-gray-500 text-center my-4">— OR —</p>

            <form onSubmit={handleManualFplSubmit} className="flex flex-col w-full">
              <input
                type="text"
                value={manualFplId}
                onChange={(e) => setManualFplId(e.target.value)}
                placeholder="Enter FPL ID Manually"
                className="w-full bg-gray-100 border border-gray-200 rounded-xl py-3 px-4 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#2d006b]"
              />
              <button type="submit" className="bg-[#2d006b] text-white rounded-xl py-3 w-full font-semibold hover:bg-[#3d0090] transition-colors border-none">
                Use This FPL ID
              </button>
            </form>
            

            {foundFplId && (
               <div className="mt-4 w-full text-center">
                 <p className="text-sm text-gray-700 mb-2"><strong>FPL ID Found:</strong> {foundFplId}</p>
                <button
                  onClick={handleCompleteRegistration}
                  className="bg-[#2d006b] text-white rounded-xl py-3 w-full font-semibold hover:bg-[#3d0090] transition-colors"
                >
                  Complete Registration
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => setStep(1)}
              className="mt-3 py-3 px-5 rounded-xl border border-[#2d006b] text-[#2d006b] bg-transparent hover:bg-[#2d006b] hover:text-white transition-colors"
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Registration;
